import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function Outstanding() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerId, setCustomer] = useState('');
    const { isAuthenticated, companyId, companyName, branchId, permissions, token, logout } = useAuth();
    const [reportGeneratedTime, setReportGeneratedTime] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());

    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const fetchTableData = async () => {
        const now = new Date();
        const formattedDateTime =
            String(now.getDate()).padStart(2, '0') + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            now.getFullYear() + ' ' +
            now.toLocaleTimeString(); // e.g., 10:45:23 AM

        setReportGeneratedTime(formattedDateTime);

        setDataItem([])
        setLoading(true);
        try {
            const response = await fetchData('ReportApi', `/${toDate}/${companyId}`, token);
            if (response.responseCode === '1111') {
                setDataItem(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Outstanding Details:', error);
        } finally {
            setLoading(false); // ✅ Hide loading screen always 
        }

    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    // Search data
    const [isFocused, setIsFocused] = useState(false);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);;
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${day}/${month}/${year}`;

        return formattedDate;
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Outstanding');
        worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 2 }];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };

        // Title row
        worksheet.mergeCells('A1:I1');
        const headerCell = worksheet.getCell('A1');
        headerCell.value = `${companyName} - Outstanding Report From: ${formatDate(fromDate)} To: ${formatDate(toDate)} | Generated On: ${formattedDateTime}`;
        headerCell.font = { bold: true, size: 10 };
        headerCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

        // Column headers
        const headerRow = worksheet.addRow([
            'Invoice Date',
            'InvoiceNumber',
            'BL No',
            'Customer',
            'Consignee',
            'Bill Amount',
            'Paid',
            'Balance',
            'Due Days',
        ]);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = thinBorder;
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD9EAD3' }, // Light green
            };
        });

        // Data rows
        filteredDataItem.forEach((rowData) => {
            const rowValues = [
                formatDateForInput(rowData.InvoiceDate),
                rowData.InvoiceNumber,
                rowData.BLNumber,
                rowData.CustomerName,
                rowData.ConsigneeName,
                rowData.BillAmount != null ? rowData.BillAmount.toFixed(3) : '',
                rowData.PaidAmount != null ? rowData.PaidAmount.toFixed(3) : '',
                rowData.BalanceAmount != null ? rowData.BalanceAmount.toFixed(3) : '',
                rowData.DueDays ?? '',
            ];

            const row = worksheet.addRow(rowValues);

            row.eachCell((cell, colNumber) => {
                if ([6, 7, 8].includes(colNumber)) {
                    cell.alignment = { horizontal: 'right' };
                    cell.numFmt = '#,##0.000';
                } else if ([9].includes(colNumber)) {
                    cell.alignment = { horizontal: 'right' };
                }
                else {
                    cell.alignment = { horizontal: 'left' };
                }
                cell.border = thinBorder;
            });
        });

        // Totals row
        if (filteredDataItem.length > 0) {
            const totalBill = filteredDataItem.reduce((sum, item) => sum + (item.BillAmount || 0), 0);
            const totalPaid = filteredDataItem.reduce((sum, item) => sum + (item.PaidAmount || 0), 0);
            const totalBalance = filteredDataItem.reduce((sum, item) => sum + (item.BalanceAmount || 0), 0);

            const totalRow = worksheet.addRow([
                '',
                '',
                '',
                '',
                'Total',
                totalBill.toFixed(3),
                totalPaid.toFixed(3),
                totalBalance.toFixed(3),
                '',
            ]);

            totalRow.eachCell((cell, colNumber) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF4CCCC' }, // Light pink
                };
                cell.font = { bold: true };
                cell.border = thinBorder;
                if ([6, 7, 8].includes(colNumber)) {
                    cell.alignment = { horizontal: 'right' };
                    cell.numFmt = '#,##0.000';
                } else {
                    cell.alignment = { horizontal: 'center' };
                }
            });
        }

        // Auto column width
        worksheet.columns.forEach((column) => {
            column.width = 20;
        });

        // Export to file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer]),
            `OutstandingReport_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`
        );
    };



    return (
        <div
            className="w-full mx-auto"
            style={{
                width: "100%",
                marginTop: "0",
                background: "#fff",
                border: "1px solid #dcdcdc",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
        >
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div className="loader"></div>
                    <span style={{ color: 'white', marginTop: '10px', fontSize: '14px' }}>
                        Loading...
                    </span>
                </div>
            )}
            {/* ===== Report Header ===== */}
            <div
                style={{
                    background: "whitesmoke",
                    color: "black",
                    padding: "5px 10px",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    marginTop: '-30px',
                    height: '30px',
                    textTransform: 'uppercase'
                }}
            >
                Outstanding Report
            </div>

            {/* ===== Filter Section ===== */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 15px",
                    borderBottom: "1px solid #eee",
                    background: "#fafafa",
                }}
            >
                {/* Search */}
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "260px" : "200px",
                        height: "30px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "0 10px",
                        transition: "width 0.3s",
                        outline: "none",
                        fontSize: "13px",
                        marginLeft: '-15px'
                    }}
                />
                {reportGeneratedTime && (
                    <div className="text-gray-600" style={{ marginTop: '5px', marginRight: '100px', fontSize: '11px', color: 'red' }}>
                        Report Generated: {reportGeneratedTime}
                    </div>
                )}

                {/* Dates + Buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* From Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>From</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>To</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* Refresh */}
                    <Button
                        onClick={fetchTableData}
                        style={{
                            background: "#2f4050",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                        }}
                    >
                        Refresh
                    </Button>

                    {/* Excel */}
                    <Button
                        onClick={handleExportExcel}
                        style={{
                            background: "#00695c",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                        }}
                    >
                        Excel
                    </Button>
                </div>
            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '500px', overflowY: 'auto' }}>

                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#102E50' }}>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Invoice Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Invoice No</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Bl No</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Customer</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Consignee</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', textAlign: 'right', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Bill Amount</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', textAlign: 'right', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Paid </TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', textAlign: 'right', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Balance</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '10px', color: 'whitesmoke', textAlign: 'right', padding: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>Due Days</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDataItem.map((data, index) => (
                                <TableRow key={data.id}>
                                    <TableCell sx={{ padding: '3px', fontSize: '11.5px' }}>{formatDate(data.InvoiceDate)}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '11px', fontWeight: 'bold' }}>
                                        <a
                                            href={`/InvoiceDetails/${data.InvoiceId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#aa5107ff' }}
                                        >
                                            {data.InvoiceNumber}
                                        </a>
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '11px' }}>{data.BLNumber}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '11px' }}>{data.CustomerName}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '11px' }}>{data.ConsigneeName}</TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                        {data.BillAmount?.toFixed(3) ?? "--"}
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                        {data.PaidAmount?.toFixed(3) ?? "--"}
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>
                                        {data.BalanceAmount?.toFixed(3) ?? "--"}
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '11.5px' }}>{data.DueDays}</TableCell>
                                </TableRow>
                            ))}

                            {/* Totals Row */}
                            {filteredDataItem.length > 0 && (
                                <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                    <TableCell colSpan={5} sx={{ padding: '3px', fontWeight: 'bold' }}>
                                        Total
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>
                                        {filteredDataItem.reduce((sum, item) => sum + (item.BillAmount || 0), 0).toFixed(3)}
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>
                                        {filteredDataItem.reduce((sum, item) => sum + (item.PaidAmount || 0), 0).toFixed(3)}
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>
                                        {filteredDataItem.reduce((sum, item) => sum + (item.BalanceAmount || 0), 0).toFixed(3)}
                                    </TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>
                                        {/* Example: show total due days or leave blank */}
                                        {/* filteredDataItem.reduce((sum, item) => sum + (item.DueDays || 0), 0) */}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            </div>
        </div >
    );
}

export default Outstanding;
